/* eslint-disable */
import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Group,
  Select,
  Stack,
  TextInput,
  Textarea,
  MultiSelect,
  Loader,
  Center,
  Text,
  Checkbox,
  NumberInput,
  Switch,
  Paper,
  ActionIcon,
} from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { z } from "zod";
import { showNotification } from "@mantine/notifications";
import axios from "axios";
import { Check, ExclamationMark } from "tabler-icons-react";
import { IconPlus, IconTrash } from "@tabler/icons-react";

import { useGetDepartments } from "api/department/getAllDepartment";
import { useEditProgram } from "api/program/updateprogram";
import { useGetProgramId } from "api/program/getbyIdprogram";
import { useGetUser } from "api/users/getUser";
import { useGetOrg } from "api/organizations/getOrg";
import { useGetOrganizations } from "api/superAdmin/organizationManagement";
import { useUploadFile } from "api/upload/uploadFile";
import { useGetSupervisors } from "api/users/supervisior";
import { useUser } from "hooks/useUser";
import { useGetUserPermissions } from "api/permission/getpermission";
import { CANADA_TIMEZONES, DEFAULT_ORG_TIMEZONE } from "utils/timezoneDate";

type UpdateFormProgramProps = {
  onClose: () => void;
  programId?: string;
};

interface RecurringConfig {
  id: string;
  recurrenceType: "day" | "weekly" | "biweekly" | "monthly";
  interval: number;
  selectedDays: string[];
  selectedDate?: string;
  startTime: string;
  endTime: string;
  totalHours: number;
}

const timeRegex = /^([01]\d|2[0-3]):([0-5]\d)$/;

// Helper: Compute total hours from start/end time
const computeTotalHours = (startTime: string, endTime: string): number => {
  if (!startTime || !endTime) return 0;
  const [startHour, startMin] = startTime.split(":").map(Number);
  const [endHour, endMin] = endTime.split(":").map(Number);

  const startInMinutes = startHour * 60 + startMin;
  const endInMinutes = endHour * 60 + endMin;

  let diff = endInMinutes - startInMinutes;
  if (diff < 0) diff += 24 * 60; // Handle overnight shifts

  return parseFloat((diff / 60).toFixed(2));
};

// Helper: Extract organization working config
const extractOrgWorkingConfig = (orgData: any) => {
  if (!orgData) return null;

  const orgWorkingHours = orgData.workingHours;
  if (!orgWorkingHours) return null;

  // Handle array format (new format with recurring support)
  if (Array.isArray(orgWorkingHours)) {
    // Find default (non-recurring) config
    const defaultConfig = orgWorkingHours.find(
      (wh: any) => !wh.date && wh.type !== "recurring"
    );
    if (defaultConfig) {
      return {
        startTime: defaultConfig.startTime || null,
        endTime: defaultConfig.endTime || null,
        totalHours: defaultConfig.totalHours || null,
      };
    }
    // Fallback to first config if no default
    if (orgWorkingHours.length > 0) {
      const first = orgWorkingHours[0];
      return {
        startTime: first.startTime || null,
        endTime: first.endTime || null,
        totalHours: first.totalHours || null,
      };
    }
    return null;
  }

  // Handle old object format
  return {
    startTime: orgWorkingHours.startTime || null,
    endTime: orgWorkingHours.endTime || null,
    totalHours: orgWorkingHours.totalHours || null,
  };
};

const programSchema = z
  .object({
    name: z.string().trim().min(1, { message: "Please enter program name" }),
    description: z
      .string()
      .trim()
      .min(1, { message: "Please enter description" }),
    status: z.enum(["active", "inactive"]).default("active"),
    parentDepartmentId: z
      .string()
      .min(1, { message: "Please select parent department" }),
    organization: z.string().optional().nullable(),
    members: z.array(z.string()).default([]),
    supervisor: z.string().optional().nullable(),
    handbook: z.string().optional().nullable(),
    programLogo: z.string().optional().nullable(),
    sameAsOrg: z.boolean().optional(),

    // Working hours
    startTime: z.string().optional().nullable(),
    endTime: z.string().optional().nullable(),
    totalHours: z.number().optional().nullable(),

    // Break config
    breakTimeEnabled: z.boolean().optional(),
    hours: z.number().optional().nullable(),
    breakStart: z.string().optional().nullable(),
    breakEnd: z.string().optional().nullable(),
    breakType: z.enum(["duration", "timeRange"]).optional(),
    breakDurationHours: z.number().optional(),

    // Overtime config
    overtimeAllowed: z.boolean().optional(),
    maxHoursPerDay: z.number().optional().nullable(),
    maxHoursPerMonth: z.number().optional().nullable(),
    overtimeRate: z.number().optional().nullable(),
    overtimeRequiresApproval: z.boolean().optional(),

    // Late arrival config
    graceMinutes: z.number().optional().nullable(),
    lateMarkHalfDayAfterMinutes: z.number().optional().nullable(),
    lateRequiresApproval: z.boolean().optional(),
    lateDeductionEnabled: z.boolean().optional(),
    lateDeductMinutesFor: z.number().optional().nullable(),
    lateForEveryMinutes: z.number().optional().nullable(),

    // Leave-driven status automation
    leaveAutomationEnabled: z.boolean().optional(),
    shortTermLeaveMaxDays: z.number().optional().nullable(),
    longTermLeaveMinDays: z.number().optional().nullable(),

    // Early departure config
    earlyGraceMinutes: z.number().optional().nullable(),
    earlyMarkHalfDayBeforeMinutes: z.number().optional().nullable(),

    // Location config
    isLocationCheck: z.boolean().optional(),
    lng: z.string().optional().nullable(),
    lat: z.string().optional().nullable(),

    // Recurring config
    enableRecurringConfig: z.boolean().optional(),
    recurringConfigs: z.array(z.any()).optional(),
    recurringStartDate: z.string().optional(),

    // Timezone
    timezone: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (
      data.parentDepartmentId &&
      typeof data.parentDepartmentId !== "string"
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Invalid parent department",
        path: ["parentDepartmentId"],
      });
    }

    // Time validation
    if (!data.sameAsOrg) {
      if (data.startTime && !timeRegex.test(data.startTime)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Invalid time format (HH:MM)",
          path: ["startTime"],
        });
      }
      if (data.endTime && !timeRegex.test(data.endTime)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Invalid time format (HH:MM)",
          path: ["endTime"],
        });
      }
    }
  });

export default function UpdateFormProgram({
  onClose,
  programId,
}: UpdateFormProgramProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { data: departmentsData } = useGetDepartments();
  const { data: programData, isLoading: isProgramLoading } =
    useGetProgramId(programId);
  const { data: userData } = useGetUser();
  const { user } = useUser();
  const userId = user?._id ?? "";
  const { data: permResp } = useGetUserPermissions(userId);
  const permissions = permResp?.data?.permissions ?? {};
  const userOrgId = userData?.org?._id || user?.org?._id || "";
  const { data: orgData } = useGetOrg(userOrgId);
  const { data: orgsResponse, isLoading: orgsLoading } = useGetOrganizations();
  const { data: supervisorsData } = useGetSupervisors(userOrgId);
  const uploadFileMutation = useUploadFile();
  const { mutate: updateProgram } = useEditProgram();

  const form = useForm({
    initialValues: {
      name: "",
      description: "",
      status: "active" as "active" | "inactive",
      parentDepartmentId: "",
      organization: "",
      members: [] as string[],
      supervisor: "" as string | null,
      handbook: "" as string | null,
      programLogo: null as string | null,
      sameAsOrg: true,

      // Working hours
      startTime: null as string | null,
      endTime: null as string | null,
      totalHours: null as number | null,

      // Break config
      breakTimeEnabled: false,
      hours: null as number | null,
      breakStart: null as string | null,
      breakEnd: null as string | null,
      breakType: "duration" as "duration" | "timeRange",
      breakDurationHours: 1,

      // Overtime config
      overtimeAllowed: false,
      maxHoursPerDay: null as number | null,
      maxHoursPerMonth: null as number | null,
      overtimeRate: null as number | null,
      overtimeRequiresApproval: false,

      // Late arrival config
      graceMinutes: null as number | null,
      lateMarkHalfDayAfterMinutes: null as number | null,
      lateRequiresApproval: false,
      lateDeductionEnabled: false,
      lateDeductMinutesFor: null as number | null,
      lateForEveryMinutes: null as number | null,

      // Leave-driven status automation
      leaveAutomationEnabled: true,
      shortTermLeaveMaxDays: 5 as number | null,
      longTermLeaveMinDays: 30 as number | null,

      // Early departure config
      earlyGraceMinutes: null as number | null,
      earlyMarkHalfDayBeforeMinutes: null as number | null,

      // Location config
      isLocationCheck: false,
      lng: null as string | null,
      lat: null as string | null,

      // Recurring config
      enableRecurringConfig: false,
      recurringConfigs: [] as RecurringConfig[],
      recurringStartDate: undefined as string | undefined,
      timezone: DEFAULT_ORG_TIMEZONE,
    },
    validate: zodResolver(programSchema),
  });

  const departmentsRaw = departmentsData?.data ?? departmentsData ?? [];
  const departments: any[] = Array.isArray(departmentsRaw)
    ? departmentsRaw
    : [];

  const departmentOptions =
    departments?.map((d) => ({
      value: String(d._id ?? d.id ?? ""),
      label: String(d.name ?? d.label ?? ""),
    })) ?? [];

  const membersFromOrg = useMemo(() => {
    const map: Record<string, any[]> = {};
    if (
      orgsResponse?.organizations &&
      Array.isArray(orgsResponse.organizations)
    ) {
      for (const o of orgsResponse.organizations) {
        const key = String(o.id ?? o.id ?? "");
        map[key] = Array.isArray(o.members) ? o.members : [];
      }
    }
    return map;
  }, [orgsResponse]);

  const fallbackMembers = useMemo(() => {
    const base = orgData?.members ?? [];
    return (base || []).map((u: any) => ({
      value: String(u._id ?? u.id ?? ""),
      label: u.firstName
        ? `${u.firstName} ${u.lastName ?? ""}`
        : u.email ?? u.name ?? "Unknown",
      role: u.role,
    }));
  }, [orgData]);

  const buildDedupe = (arrs: Array<{ value: string; label: string }[]>) => {
    const deduped: { value: string; label: string }[] = [];
    for (const arr of arrs) {
      for (const o of arr) {
        if (!deduped.find((d) => d.value === o.value)) deduped.push(o);
      }
    }
    return deduped;
  };

  const buildOptionsForOrg = (orgId?: string | null) => {
    const orgMembers = (orgId ? membersFromOrg[String(orgId)] : []) ?? [];
    const orgMemberOptions = orgMembers.map((m: any) => ({
      value: String(m._id ?? m.id ?? ""),
      label:
        m.firstName || m.email
          ? `${m.firstName ? `${m.firstName} ${m.lastName ?? ""}` : m.email}`
          : String(m._id ?? m.id ?? ""),
      role: m.role,
    }));
    return buildDedupe([orgMemberOptions, fallbackMembers]);
  };

  const [handbookFile, setHandbookFile] = useState<File | null>(null);
  const [handbookPreview, setHandbookPreview] = useState<string>("");
  const [localUserOptions, setLocalUserOptions] = useState<
    { value: string; label: string }[]
  >([]);

  const supervisorOptions = useMemo(() => {
    if (supervisorsData?.data && Array.isArray(supervisorsData.data)) {
      return supervisorsData.data.map((s: any) => ({
        value: String(s._id ?? ""),
        label: s.firstName
          ? `${s.firstName} ${s.lastName ?? ""}`
          : s.email ?? "Unknown",
      }));
    }
    return [];
  }, [supervisorsData]);

  const membersOptions = useMemo(() => {
    if (form.values.supervisor) {
      return localUserOptions.filter((o) => o.value !== form.values.supervisor);
    }
    return localUserOptions;
  }, [localUserOptions, form.values.supervisor]);

  useEffect(() => {
    const userOrgId = user?.org?._id ?? user?.org?._id ?? "";
    if (userOrgId && !form.values.organization) {
      form.setFieldValue("organization", String(userOrgId));
    }
  }, [user]);

  useEffect(() => {
    const opts = form.values.organization
      ? buildOptionsForOrg(form.values.organization)
      : fallbackMembers;
    setLocalUserOptions(opts);
  }, [form.values.organization, orgsResponse, orgData, fallbackMembers]);

  useEffect(() => {
    if (programData?.data) {
      const p = programData.data as any;

      const programMemberIds: string[] =
        p.members
          ?.map((m: any) => {
            if (m?.memberId && typeof m.memberId === "object") {
              return String(m.memberId._id ?? m.memberId);
            }

            if (m?.memberId && typeof m.memberId === "string") {
              return String(m.memberId);
            }

            if (m?._id) {
              return String(m._id);
            }

            if (typeof m === "string") {
              return m;
            }
            return "";
          })
          .filter(Boolean) || [];

      // Extract working hours config (new array format)
      let workingHoursData: any = null;
      let recurringItems: any[] = [];
      if (Array.isArray(p.workingHours) && p.workingHours.length > 0) {
        const defaultWH = p.workingHours.find(
          (wh: any) => wh.type === "default"
        );
        if (defaultWH) {
          workingHoursData = defaultWH;
        }

        // Collect ALL recurring configs
        recurringItems = p.workingHours.filter(
          (wh: any) => wh.type === "recurring" || wh.recurringenabled
        );
      }

      // Determine sameAsOrg: if API returns it, use that; otherwise infer from presence of custom configs
      let sameAsOrgValue: boolean;
      if (typeof p.sameAsOrg === "boolean") {
        sameAsOrgValue = p.sameAsOrg;
      } else {
        // Infer from presence of custom attendance configurations
        const hasCustomConfigs =
          (Array.isArray(p.workingHours) && p.workingHours.length > 0) ||
          p.overtimeConfig ||
          p.lateArrivalConfig ||
          p.earlyDepartureConfig ||
          p.leaveStatusAutomation ||
          p.break ||
          (p.isLocationCheck && p.location);
        sameAsOrgValue = !hasCustomConfigs;
      }

      // Determine if break is enabled: breakConfig exists, is an object, has keys, and enabled is explicitly true
      const calculatedBreakTimeEnabled =
        p.break !== null &&
        p.break !== undefined &&
        typeof p.break === "object" &&
        Object.keys(p.break).length > 0 &&
        p.break.enabled === true;

      form.setValues({
        name: p.name || "",
        description: p.description || "",
        status: (p.status as "active" | "inactive") || "active",
        parentDepartmentId:
          p.parentDepartment?._id || p.parentDepartmentId || "",
        members: programMemberIds,
        organization:
          p.organization?._id ||
          p.organization ||
          form.values.organization ||
          "",
        supervisor: p.supervisor
          ? String(p.supervisor._id ?? p.supervisor)
          : "",
        handbook: p.handbook || "",
        programLogo: p.programLogo || null,
        sameAsOrg: sameAsOrgValue,

        // Working hours
        startTime: workingHoursData?.startTime || null,
        endTime: workingHoursData?.endTime || null,
        totalHours: workingHoursData?.totalHours || null,

        // Break config
        breakTimeEnabled: calculatedBreakTimeEnabled,
        breakType: p.break?.type || "duration",
        breakDurationHours: p.break?.durationHours || 1,
        breakStart: p.break?.startTime || null,
        breakEnd: p.break?.endTime || null,

        // Overtime config
        overtimeAllowed: p.overtimeConfig?.allowed || false,
        maxHoursPerDay: p.overtimeConfig?.maxHoursPerDay || null,
        maxHoursPerMonth: p.overtimeConfig?.maxHoursPerMonth || null,
        overtimeRate: p.overtimeConfig?.overtimeRate || null,
        overtimeRequiresApproval: p.overtimeConfig?.requiresApproval || false,

        // Late arrival config
        graceMinutes: p.lateArrivalConfig?.graceMinutes || null,
        lateMarkHalfDayAfterMinutes:
          p.lateArrivalConfig?.markAsHalfDayAfter || null,
        lateDeductionEnabled: p.lateArrivalConfig?.deductionEnabled || false,
        lateDeductMinutesFor: p.lateArrivalConfig?.deductMinutesFor || null,
        lateForEveryMinutes: p.lateArrivalConfig?.forEveryMinutes || null,

        // Leave-driven status automation
        leaveAutomationEnabled:
          typeof p.leaveStatusAutomation?.enabled === "boolean"
            ? p.leaveStatusAutomation.enabled
            : true,
        shortTermLeaveMaxDays:
          p.leaveStatusAutomation?.shortTermLeaveMaxDays ?? 5,
        longTermLeaveMinDays:
          p.leaveStatusAutomation?.longTermLeaveMinDays ?? 30,

        // Early departure config
        earlyGraceMinutes: p.earlyDepartureConfig?.graceMinutes || null,
        earlyMarkHalfDayBeforeMinutes:
          p.earlyDepartureConfig?.markHalfDayBeforeMinutes || null,

        // Location config
        isLocationCheck: p.isLocationCheck || false,
        lng: p.location?.lng ? String(p.location.lng) : null,
        lat: p.location?.lat ? String(p.location.lat) : null,

        // Recurring config
        enableRecurringConfig: recurringItems.length > 0,
        recurringConfigs: recurringItems.map((item: any) => ({
          id: item._id ?? `recurring-${Date.now()}-${Math.random()}`,
          recurrenceType: (item.recurrenceType || "weekly") as
            | "day"
            | "weekly"
            | "biweekly"
            | "monthly",
          interval: Number(item.interval) || 1,
          selectedDays: Array.isArray(item.selectedDays)
            ? item.selectedDays
            : [],
          selectedDate: item.date || undefined,
          startTime: item.startTime || "",
          endTime: item.endTime || "",
          totalHours: Number(item.totalHours) || 0,
        })) as RecurringConfig[],
        recurringStartDate: undefined,
        timezone: p.timezone || DEFAULT_ORG_TIMEZONE,
      });

      setHandbookPreview(p.handbook || "");
    }
  }, [programData]);

  // Auto-compute totalHours when startTime or endTime changes
  useEffect(() => {
    if (
      form.values.startTime &&
      form.values.endTime &&
      !form.values.sameAsOrg
    ) {
      const computed = computeTotalHours(
        form.values.startTime,
        form.values.endTime
      );
      if (computed !== form.values.totalHours) {
        form.setFieldValue("totalHours", computed);
      }
    }
  }, [form.values.startTime, form.values.endTime, form.values.sameAsOrg]);

  // Auto-compute totalHours for each recurring configuration entry
  useEffect(() => {
    if (
      !form.values.sameAsOrg &&
      form.values.enableRecurringConfig &&
      form.values.recurringConfigs.length > 0
    ) {
      const updatedConfigs = form.values.recurringConfigs.map((config) => {
        if (config.startTime && config.endTime) {
          const computed = computeTotalHours(config.startTime, config.endTime);
          if (computed !== config.totalHours) {
            return { ...config, totalHours: computed };
          }
        }
        return config;
      });
      if (JSON.stringify(updatedConfigs) !== JSON.stringify(form.values.recurringConfigs)) {
        form.setFieldValue("recurringConfigs", updatedConfigs);
      }
    }
  }, [
    form.values.recurringConfigs.map((c) => c.startTime).join(","),
    form.values.recurringConfigs.map((c) => c.endTime).join(","),
    form.values.enableRecurringConfig,
    form.values.sameAsOrg,
  ]);

  // Extract org working config when sameAsOrg changes
  useEffect(() => {
    if (form.values.sameAsOrg && orgData) {
      const orgConfig = extractOrgWorkingConfig(orgData);
      if (orgConfig) {
        form.setFieldValue("startTime", orgConfig.startTime);
        form.setFieldValue("endTime", orgConfig.endTime);
        form.setFieldValue("totalHours", orgConfig.totalHours);
      }
    }
  }, [form.values.sameAsOrg, orgData]);

  const isUsingOrg = form.values.sameAsOrg;

  const addRecurringConfig = () => {
    form.insertListItem("recurringConfigs", {
      id: `recurring-${Date.now()}-${Math.random()}`,
      recurrenceType: "weekly",
      interval: 1,
      selectedDays: [],
      selectedDate: undefined,
      startTime: "08:00",
      endTime: "16:00",
      totalHours: 8,
    } as RecurringConfig);
  };

  const removeRecurringConfig = (index: number) => {
    form.removeListItem("recurringConfigs", index);
  };

  // Auto-add the first recurring config entry when the checkbox is toggled on
  useEffect(() => {
    if (
      form.values.enableRecurringConfig &&
      form.values.recurringConfigs.length === 0
    ) {
      addRecurringConfig();
    }
  }, [form.values.enableRecurringConfig]);

  const handleMembersChange = (val: string[]) => {
    form.setFieldValue("members", val ?? []);
  };

  const handleHandbookChange = (file?: File | null) => {
    if (!file) {
      setHandbookFile(null);
      setHandbookPreview("");
      form.setFieldValue("handbook", "");
      return;
    }
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      showNotification({
        title: "File too large",
        message: "Handbook file must be less than 5MB",
        color: "red",
      });
      return;
    }
    setHandbookFile(file);
    setHandbookPreview(file.name);
  };

  const handleLogoChange = (file?: File | null) => {
    if (!file) {
      form.setFieldValue("programLogo", null);
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      form.setFieldValue("programLogo", reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  type FormValues = typeof form.values;

  const handleSubmit = async (values: FormValues) => {
    if (!programId) return;
    const payload: any = {
      name: values.name?.trim(),
      status: values.status,
    };

    if (values.description) payload.description = values.description?.trim();
    payload.parentDepartmentId = values.parentDepartmentId || null;
    if (values.organization) payload.ogrId = values.organization;
    if (values.supervisor) payload.supervisor = values.supervisor;

    const normalizedMembers = Array.isArray(values.members)
      ? values.members
      : values.members
      ? [String(values.members)]
      : [];
    if (normalizedMembers.length > 0) payload.members = normalizedMembers;

    let handbookUrl = values.handbook || null;
    if (handbookFile) {
      try {
        const uploadResult = await uploadFileMutation.mutateAsync(handbookFile);
        handbookUrl =
          uploadResult?.data?.url ?? uploadResult?.data ?? handbookUrl;
      } catch (err: any) {
        showNotification({
          title: "Handbook upload failed",
          message:
            err?.response?.data?.message || "Failed to upload handbook file",
          color: "red",
        });
        return;
      }
    }
    if (handbookUrl) payload.handbook = handbookUrl;

    if (
      values.programLogo &&
      typeof values.programLogo === "string" &&
      values.programLogo.startsWith("data:")
    ) {
      try {
        const res = await fetch(values.programLogo);
        const blob = await res.blob();
        const file = new File([blob], "program-logo.png", { type: blob.type });
        const uploadResult = await uploadFileMutation.mutateAsync(file);
        payload.programLogo = uploadResult?.data?.url ?? uploadResult?.data;
      } catch (err: any) {
        showNotification({
          title: "Logo upload failed",
          message: err?.response?.data?.message || "Failed to upload logo",
          color: "red",
        });
        return;
      }
    } else if (values.programLogo) {
      payload.programLogo = values.programLogo;
    }

    // Attendance configurations
    payload.sameAsOrg = values.sameAsOrg || false;

    if (values.sameAsOrg) {
      payload.workingHours = null;
      payload.overtimeConfig = null;
      payload.lateArrivalConfig = null;
      payload.earlyDepartureConfig = null;
      payload.leaveStatusAutomation = null;
      payload.break = null;
    } else {
      // Build working hours array (new format)
      const workingHours: any[] = [];

      // Add default working hours
      if (values.startTime && values.endTime) {
        const totalHours =
          values.totalHours !== undefined && values.totalHours !== null
            ? Number(values.totalHours)
            : computeTotalHours(values.startTime, values.endTime);

        if (totalHours !== undefined) {
          workingHours.push({
            type: "default" as const,
            startTime: values.startTime,
            endTime: values.endTime,
            totalHours: Number(totalHours),
          });
        }
      }

      // Add all recurring working hours if enabled
      if (values.enableRecurringConfig && values.recurringConfigs.length > 0) {
        values.recurringConfigs.forEach((recurringConfig) => {
          const entry: any = {
            type: "recurring" as const,
            recurringenabled: true,
            recurrenceType: recurringConfig.recurrenceType,
            interval: recurringConfig.interval,
            date: recurringConfig.selectedDate,
            startTime: recurringConfig.startTime,
            endTime: recurringConfig.endTime,
            totalHours: recurringConfig.totalHours,
          };

          // Add selectedDays only for non-day recurrence types
          if (recurringConfig.recurrenceType !== "day") {
            entry.selectedDays = recurringConfig.selectedDays;
          }

          // Add monthlyPattern for monthly recurrence
          if (recurringConfig.recurrenceType === "monthly") {
            entry.monthlyPattern = "first";
          }

          workingHours.push(entry);
        });
      }

      // Set workingHours in payload
      if (workingHours.length > 0) {
        payload.workingHours = workingHours;
      } else {
        payload.workingHours = null;
      }

      // Build overtime config
      if (typeof values.overtimeAllowed === "boolean") {
        payload.overtimeConfig = {
          allowed: values.overtimeAllowed,
          maxHoursPerDay:
            values.maxHoursPerDay !== undefined &&
            values.maxHoursPerDay !== null
              ? Number(values.maxHoursPerDay)
              : undefined,
          maxHoursPerMonth:
            values.maxHoursPerMonth !== undefined &&
            values.maxHoursPerMonth !== null
              ? Number(values.maxHoursPerMonth)
              : undefined,
          overtimeRate:
            values.overtimeRate !== undefined && values.overtimeRate !== null
              ? Number(values.overtimeRate)
              : undefined,
          requiresApproval: values.overtimeRequiresApproval || false,
        };
      } else {
        payload.overtimeConfig = null;
      }

      // Build late arrival config
      if (values.graceMinutes !== undefined && values.graceMinutes !== null) {
        payload.lateArrivalConfig = {
          graceMinutes: Number(values.graceMinutes),
          markAsHalfDayAfter:
            values.lateMarkHalfDayAfterMinutes !== undefined &&
            values.lateMarkHalfDayAfterMinutes !== null
              ? Number(values.lateMarkHalfDayAfterMinutes)
              : undefined,
          deductionEnabled: values.lateDeductionEnabled || false,
          deductMinutesFor:
            values.lateDeductMinutesFor !== undefined &&
            values.lateDeductMinutesFor !== null
              ? Number(values.lateDeductMinutesFor)
              : undefined,
          forEveryMinutes:
            values.lateForEveryMinutes !== undefined &&
            values.lateForEveryMinutes !== null
              ? Number(values.lateForEveryMinutes)
              : undefined,
        };
      } else {
        payload.lateArrivalConfig = null;
      }

      // Build leave-driven status automation config
      payload.leaveStatusAutomation = {
        enabled: values.leaveAutomationEnabled ?? true,
        shortTermLeaveMaxDays:
          values.shortTermLeaveMaxDays !== undefined &&
          values.shortTermLeaveMaxDays !== null
            ? Number(values.shortTermLeaveMaxDays)
            : undefined,
        longTermLeaveMinDays:
          values.longTermLeaveMinDays !== undefined &&
          values.longTermLeaveMinDays !== null
            ? Number(values.longTermLeaveMinDays)
            : undefined,
      };

      // Build early departure config
      if (
        values.earlyGraceMinutes !== undefined &&
        values.earlyGraceMinutes !== null
      ) {
        payload.earlyDepartureConfig = {
          graceMinutes: Number(values.earlyGraceMinutes),
          markHalfDayBeforeMinutes:
            values.earlyMarkHalfDayBeforeMinutes !== undefined &&
            values.earlyMarkHalfDayBeforeMinutes !== null
              ? Number(values.earlyMarkHalfDayBeforeMinutes)
              : undefined,
        };
      }

      // Build break config - support both duration and timeRange
      if (values.breakTimeEnabled) {
        if (
          values.breakType === "duration" &&
          values.breakDurationHours !== undefined &&
          values.breakDurationHours !== null
        ) {
          payload.break = {
            enabled: true,
            type: "duration",
            durationHours: Number(values.breakDurationHours),
          };
        } else if (
          values.breakType === "timeRange" &&
          values.breakStart &&
          values.breakEnd
        ) {
          payload.break = {
            enabled: true,
            type: "timeRange",
            startTime: values.breakStart,
            endTime: values.breakEnd,
          };
        }
      } else {
        // Send null instead of undefined so backend can unset the break field
        payload.break = null;
      }

      // Build location config
      if (values.isLocationCheck) {
        payload.isLocationCheck = true;
        if (values.lng && values.lat) {
          payload.location = {
            lng:
              typeof values.lng === "string"
                ? parseFloat(values.lng)
                : values.lng,
            lat:
              typeof values.lat === "string"
                ? parseFloat(values.lat)
                : values.lat,
          };
        }
      } else {
        payload.isLocationCheck = false;
        payload.location = null;
      }
    }

    // Timezone
    if (values.timezone) {
      payload.timezone = values.timezone;
    }

    setIsSubmitting(true);
    updateProgram(
      { id: programId, payload },
      {
        onSuccess: () => {
          showNotification({
            title: "Program Updated",
            message: "Program updated successfully.",
            color: "green",
            icon: <Check />,
          });
          setIsSubmitting(false);
          onClose();
        },
        onError: (err: unknown) => {
          setIsSubmitting(false);
          if (axios.isAxiosError(err)) {
            if (err.response?.status === 409) {
              form.setFieldError(
                "name",
                "A program with this name already exists"
              );
              return;
            }
          }

          showNotification({
            title: "Server Error",
            message: "Please try again later",
            color: "red",
            icon: <ExclamationMark />,
          });
        },
      }
    );
  };

  const getNested = (obj: any, path: string) => {
    if (!obj || !path) return undefined;
    return path.split(".").reduce((acc: any, key: string) => {
      return acc && acc[key] !== undefined ? acc[key] : undefined;
    }, obj);
  };

  const hasPermission = (permissionKey: string) => {
    const value = getNested(permissions, permissionKey);

    return (
      value === true ||
      (typeof value === "object" &&
        value !== null &&
        Object.values(value).some(Boolean))
    );
  };

  const WORKINGHOURS_PERMISSION = "configuration_management.program_configrations";

  if (isProgramLoading || orgsLoading) {
    return (
      <Center py={40}>
        <Loader />
      </Center>
    );
  }

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack>
        <TextInput
          label="Program Name"
          withAsterisk
          placeholder="Enter program name"
          disabled={isProgramLoading || isSubmitting}
          {...form.getInputProps("name")}
        />

        <Select
          label="Timezone"
          description="Attendance start/end times are interpreted in this timezone"
          placeholder="Select timezone"
          data={CANADA_TIMEZONES}
          searchable
          disabled={isProgramLoading || isSubmitting}
          {...form.getInputProps("timezone")}
        />

        <Textarea
          label="Description"
          withAsterisk
          placeholder="Short description (optional)"
          minRows={3}
          disabled={isProgramLoading || isSubmitting}
          {...form.getInputProps("description")}
        />

        <div style={{ marginBottom: 15 }}>
          <Text size="sm" weight={500} mb={5}>
            Handbook (PDF/Document - Max 5MB)
          </Text>

          <input
            type="file"
            accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            onChange={(e) => {
              const file = e.target.files?.[0] ?? null;
              handleHandbookChange(file ?? null);
            }}
            disabled={isSubmitting}
          />

          {handbookPreview && (
            <Text size="sm" color="blue" mt={5}>
              Selected: {handbookPreview}
            </Text>
          )}

          {form.values.handbook && !handbookFile && (
            <div style={{ marginTop: 8 }}>
              <Text size="sm" color="dimmed">
                Current:{" "}
                <Text
                  component="a"
                  href={form.values.handbook}
                  target="_blank"
                  rel="noopener noreferrer"
                  color="blue"
                >
                  View Handbook
                </Text>
              </Text>
              <Button
                size="xs"
                variant="subtle"
                color="red"
                mt={4}
                onClick={() => {
                  form.setFieldValue("handbook", "");
                  setHandbookPreview("");
                }}
              >
                Remove Handbook
              </Button>
            </div>
          )}
        </div>

        <Select
          label="Status"
          withAsterisk
          data={[
            { value: "active", label: "Active" },
            { value: "inactive", label: "Inactive" },
          ]}
          disabled={isSubmitting}
          {...form.getInputProps("status")}
        />

        <Select
          label="Parent Department"
          withAsterisk
          placeholder="Select department (optional)"
          data={departmentOptions}
          searchable
          clearable
          disabled={isSubmitting}
          {...form.getInputProps("parentDepartmentId")}
        />

        <Select
          label="Supervisor"
          data={supervisorOptions}
          clearable
          mb={15}
          placeholder="Select a supervisor"
          value={form.values.supervisor ?? ""}
          onChange={(val) => form.setFieldValue("supervisor", val ?? "")}
        />

        <MultiSelect
          key={form.values.organization}
          label="Members"
          data={membersOptions}
          searchable
          clearable
          placeholder="Select members (optional)"
          value={form.values.members}
          onChange={handleMembersChange}
        />

        {/* Logo upload */}
        <div style={{ marginBottom: 20 }}>
          <Text size="sm" weight={700} mb={5}>
            Program Logo
          </Text>

          <input
            type="file"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files?.[0] ?? null;
              if (!file) return;
              const reader = new FileReader();
              reader.onloadend = () => {
                form.setFieldValue("programLogo", reader.result as string);
              };
              reader.readAsDataURL(file);
            }}
            disabled={isSubmitting}
          />

          {form.values.programLogo && (
            <img
              src={form.values.programLogo}
              alt="Program Logo Preview"
              style={{
                width: 90,
                height: 90,
                marginTop: 10,
                borderRadius: 8,
                border: "1px solid #ddd",
                padding: 5,
                objectFit: "contain",
              }}
            />
          )}
        </div>

        {/* Attendance Configuration Section */}

        {hasPermission(WORKINGHOURS_PERMISSION) && (
          <div
            style={{
              marginTop: 20,
              paddingTop: 20,
              borderTop: "2px solid #ddd",
            }}
          >
            <Text size="lg" weight={600} mb="md">
              Attendance Configuration
            </Text>

            <Checkbox
              label="Same as Organization Configuration"
              mb={12}
              checked={form.values.sameAsOrg}
              onChange={(e) =>
                form.setFieldValue("sameAsOrg", e.currentTarget.checked)
              }
            />

            <div
              style={{
                display: "flex",
                gap: 12,
                marginTop: 8,
                marginBottom: 8,
                flexWrap: "wrap",
              }}
            >
              <TextInput
                label="Start Time"
                placeholder="08:00"
                type="time"
                style={{ flex: 1, minWidth: 160 }}
                {...form.getInputProps("startTime")}
                disabled={isUsingOrg}
              />

              <TextInput
                label="End Time"
                placeholder="16:00"
                type="time"
                style={{ flex: 1, minWidth: 160 }}
                {...form.getInputProps("endTime")}
                disabled={isUsingOrg}
              />

              <NumberInput
                label="Total Hours"
                style={{ width: 140 }}
                value={form.values.totalHours ?? undefined}
                precision={2}
                step={0.01}
                min={0}
                onChange={(val) =>
                  form.setFieldValue("totalHours", val ?? null)
                }
                disabled={isUsingOrg}
                readOnly
              />
            </div>

            <div style={{ marginTop: 16 }}>
              <Text size="md" weight={600} mb="md">
                Break Time Configuration
              </Text>

              <Switch
                label="Break Time Enabled"
                {...form.getInputProps("breakTimeEnabled", {
                  type: "checkbox",
                })}
                disabled={isUsingOrg}
                mb="md"
              />

              <Group>
                <Select
                  label="Break Type"
                  data={[
                    { value: "duration", label: "Duration" },
                    { value: "timeRange", label: "Time Range" },
                  ]}
                  {...form.getInputProps("breakType")}
                  disabled={!form.values.breakTimeEnabled || isUsingOrg}
                  style={{ minWidth: 200 }}
                />
                {form.values.breakType === "duration" ? (
                  <NumberInput
                    label="Break Duration (hours)"
                    value={form.values.breakDurationHours ?? undefined}
                    min={0}
                    precision={2}
                    step={0.25}
                    onChange={(val) =>
                      form.setFieldValue("breakDurationHours", val ?? 1)
                    }
                    disabled={!form.values.breakTimeEnabled || isUsingOrg}
                  />
                ) : (
                  <Group grow>
                    <TextInput
                      label="Break Start Time"
                      type="time"
                      {...form.getInputProps("breakStart")}
                      disabled={!form.values.breakTimeEnabled || isUsingOrg}
                    />
                    <TextInput
                      label="Break End Time"
                      type="time"
                      {...form.getInputProps("breakEnd")}
                      disabled={!form.values.breakTimeEnabled || isUsingOrg}
                    />
                  </Group>
                )}
              </Group>
            </div>

            {/* Recurring Schedule Configuration */}
            <Checkbox
              label="Recurring time configuration (optional)"
              mt="md"
              disabled={isUsingOrg}
              checked={form.values.enableRecurringConfig || false}
              onChange={(e) =>
                form.setFieldValue(
                  "enableRecurringConfig",
                  e.currentTarget.checked
                )
              }
            />

            {form.values.enableRecurringConfig && !isUsingOrg && (
              <>
                {form.values.recurringConfigs.map((config, index) => (
                  <Paper key={config.id} p="md" withBorder mt="md">
                    <Group position="apart" mb="md">
                      <Text weight={500}>Recurring Configuration {index + 1}</Text>
                      {form.values.recurringConfigs.length > 1 && (
                        <ActionIcon
                          color="red"
                          variant="light"
                          onClick={() => removeRecurringConfig(index)}
                          title="Remove recurring configuration"
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      )}
                    </Group>

                    <Group grow>
                      <Select
                        label="Recurrence Type"
                        data={[
                          { value: "day", label: "Date" },
                          { value: "weekly", label: "Week" },
                          { value: "biweekly", label: "Bi-Week" },
                        ]}
                        {...form.getInputProps(`recurringConfigs.${index}.recurrenceType`)}
                      />
                      <NumberInput
                        label="Interval"
                        min={1}
                        {...form.getInputProps(`recurringConfigs.${index}.interval`)}
                      />
                      <TextInput
                        label="Select Date"
                        type="date"
                        required
                        {...form.getInputProps(`recurringConfigs.${index}.selectedDate`)}
                      />
                    </Group>

                    {form.values.recurringConfigs[index].recurrenceType !== "day" && (
                      <Group grow mt="md">
                        <MultiSelect
                          label="Selected Days"
                          data={[
                            { value: "monday", label: "Monday" },
                            { value: "tuesday", label: "Tuesday" },
                            { value: "wednesday", label: "Wednesday" },
                            { value: "thursday", label: "Thursday" },
                            { value: "friday", label: "Friday" },
                            { value: "saturday", label: "Saturday" },
                            { value: "sunday", label: "Sunday" },
                          ]}
                          placeholder="Select days"
                          required
                          {...form.getInputProps(`recurringConfigs.${index}.selectedDays`)}
                        />
                      </Group>
                    )}

                    <Group grow mt="md">
                      <TextInput
                        label="Start Time"
                        type="time"
                        {...form.getInputProps(`recurringConfigs.${index}.startTime`)}
                      />
                      <TextInput
                        label="End Time"
                        type="time"
                        {...form.getInputProps(`recurringConfigs.${index}.endTime`)}
                      />
                      <NumberInput
                        label="Total Hours"
                        readOnly
                        step={0.25}
                        precision={2}
                        {...form.getInputProps(`recurringConfigs.${index}.totalHours`)}
                      />
                    </Group>
                  </Paper>
                ))}

                <Button
                  variant="outline"
                  leftIcon={<IconPlus size={16} />}
                  onClick={addRecurringConfig}
                  type="button"
                  mt="md"
                >
                  Add Recurring Configuration
                </Button>
              </>
            )}

            {/* Overtime Configuration Section */}
            <div
              style={{
                marginTop: 16,
                paddingTop: 16,
                borderTop: "1px solid #eee",
              }}
            >
              <Text size="md" weight={600} mb="md">
                Overtime Configuration
              </Text>

              <Switch
                label="Overtime Allowed"
                checked={!!form.values.overtimeAllowed}
                onChange={(event) =>
                  form.setFieldValue(
                    "overtimeAllowed",
                    event.currentTarget.checked
                  )
                }
                disabled={isUsingOrg}
                mb="md"
              />

              <Group align="flex-end">
                <NumberInput
                  label="Max Hours Per Day"
                  value={form.values.maxHoursPerDay ?? undefined}
                  precision={2}
                  step={0.25}
                  min={0}
                  onChange={(val) =>
                    form.setFieldValue("maxHoursPerDay", val ?? null)
                  }
                  disabled={isUsingOrg || !form.values.overtimeAllowed}
                />
                <NumberInput
                  label="Max Hours Per Month"
                  value={form.values.maxHoursPerMonth ?? undefined}
                  precision={2}
                  step={0.5}
                  min={0}
                  onChange={(val) =>
                    form.setFieldValue("maxHoursPerMonth", val ?? null)
                  }
                  disabled={isUsingOrg || !form.values.overtimeAllowed}
                />
                <NumberInput
                  label="Overtime Rate (multiplier)"
                  value={form.values.overtimeRate ?? undefined}
                  precision={2}
                  step={0.25}
                  min={1}
                  onChange={(val) =>
                    form.setFieldValue("overtimeRate", val ?? null)
                  }
                  disabled={isUsingOrg || !form.values.overtimeAllowed}
                />
              </Group>

              <Checkbox
                label="Requires Approval"
                mt="md"
                checked={!!form.values.overtimeRequiresApproval}
                onChange={(e) =>
                  form.setFieldValue(
                    "overtimeRequiresApproval",
                    e.currentTarget.checked
                  )
                }
                disabled={isUsingOrg || !form.values.overtimeAllowed}
              />
            </div>

            {/* Late Arrival Configuration */}
            <div
              style={{
                marginTop: 16,
                paddingTop: 16,
                borderTop: "1px solid #eee",
              }}
            >
              <Text size="md" weight={600} mb="md">
                Late Arrival Configuration
              </Text>

              <Group align="flex-end">
                <NumberInput
                  label="Grace Minutes"
                  value={form.values.graceMinutes ?? undefined}
                  precision={0}
                  step={5}
                  min={0}
                  onChange={(val) =>
                    form.setFieldValue("graceMinutes", val ?? null)
                  }
                  disabled={isUsingOrg}
                />
                <NumberInput
                  label="Mark Half Day After (minutes)"
                  value={form.values.lateMarkHalfDayAfterMinutes ?? undefined}
                  precision={0}
                  step={15}
                  min={0}
                  onChange={(val) =>
                    form.setFieldValue(
                      "lateMarkHalfDayAfterMinutes",
                      val ?? null
                    )
                  }
                  disabled={isUsingOrg}
                />
              </Group>

              <Checkbox
                label="Late Deduction Enabled"
                mt="md"
                checked={!!form.values.lateDeductionEnabled}
                onChange={(e) =>
                  form.setFieldValue(
                    "lateDeductionEnabled",
                    e.currentTarget.checked
                  )
                }
                disabled={isUsingOrg}
              />

              {form.values.lateDeductionEnabled && (
                <Group align="flex-end" mt="md">
                  <NumberInput
                    label="Deduct Minutes For"
                    value={form.values.lateDeductMinutesFor ?? undefined}
                    precision={0}
                    step={5}
                    min={0}
                    onChange={(val) =>
                      form.setFieldValue("lateDeductMinutesFor", val ?? null)
                    }
                    disabled={isUsingOrg}
                  />
                  <NumberInput
                    label="For Every Minutes"
                    value={form.values.lateForEveryMinutes ?? undefined}
                    precision={0}
                    step={5}
                    min={1}
                    onChange={(val) =>
                      form.setFieldValue("lateForEveryMinutes", val ?? null)
                    }
                    disabled={isUsingOrg}
                  />
                </Group>
              )}
            </div>

            {/* Early Departure Configuration */}
            <div
              style={{
                marginTop: 16,
                paddingTop: 16,
                borderTop: "1px solid #eee",
              }}
            >
              <Text size="md" weight={600} mb="md">
                Early Departure Configuration
              </Text>

              <Group align="flex-end">
                <NumberInput
                  label="Grace Minutes"
                  value={form.values.earlyGraceMinutes ?? undefined}
                  precision={0}
                  step={5}
                  min={0}
                  onChange={(val) =>
                    form.setFieldValue("earlyGraceMinutes", val ?? null)
                  }
                  disabled={isUsingOrg}
                />
                <NumberInput
                  label="Mark Half Day Before (minutes)"
                  value={form.values.earlyMarkHalfDayBeforeMinutes ?? undefined}
                  precision={0}
                  step={15}
                  min={0}
                  onChange={(val) =>
                    form.setFieldValue(
                      "earlyMarkHalfDayBeforeMinutes",
                      val ?? null
                    )
                  }
                  disabled={isUsingOrg}
                />
              </Group>
            </div>

            {/* Leave Status Automation */}
            <div
              style={{
                marginTop: 16,
                paddingTop: 16,
                borderTop: "1px solid #eee",
              }}
            >
              <Text size="md" weight={600} mb="md">
                Leave Status Automation
              </Text>
              <Switch
                label="Enable leave-driven status automation"
                mb="sm"
                checked={form.values.leaveAutomationEnabled ?? true}
                onChange={(e) =>
                  form.setFieldValue(
                    "leaveAutomationEnabled",
                    e.currentTarget.checked
                  )
                }
                disabled={isUsingOrg}
              />
              <Group align="flex-end">
                <NumberInput
                  label="Short-term leave: maximum days"
                  value={form.values.shortTermLeaveMaxDays ?? undefined}
                  precision={0}
                  step={1}
                  min={0}
                  onChange={(val) =>
                    form.setFieldValue("shortTermLeaveMaxDays", val ?? null)
                  }
                  disabled={isUsingOrg || !form.values.leaveAutomationEnabled}
                />
                <NumberInput
                  label="Long-term leave: minimum days"
                  value={form.values.longTermLeaveMinDays ?? undefined}
                  precision={0}
                  step={1}
                  min={0}
                  onChange={(val) =>
                    form.setFieldValue("longTermLeaveMinDays", val ?? null)
                  }
                  disabled={isUsingOrg || !form.values.leaveAutomationEnabled}
                />
              </Group>
            </div>

            {/* Location Configuration */}
            <div
              style={{
                marginTop: 16,
                paddingTop: 16,
                borderTop: "1px solid #eee",
              }}
            >
              <Text size="md" weight={600} mb="md">
                Location-Based Check-in
              </Text>

              <Checkbox
                label="Enable Location Check"
                checked={!!form.values.isLocationCheck}
                onChange={(e) =>
                  form.setFieldValue("isLocationCheck", e.currentTarget.checked)
                }
                disabled={isUsingOrg}
                mb="md"
              />

              {form.values.isLocationCheck && !isUsingOrg && (
                <Group align="flex-end">
                  <TextInput
                    label="Latitude"
                    placeholder="e.g., 37.7749"
                    value={form.values.lat ?? ""}
                    onChange={(e) =>
                      form.setFieldValue("lat", e.currentTarget.value)
                    }
                  />
                  <TextInput
                    label="Longitude"
                    placeholder="e.g., -122.4194"
                    value={form.values.lng ?? ""}
                    onChange={(e) =>
                      form.setFieldValue("lng", e.currentTarget.value)
                    }
                  />
                </Group>
              )}
            </div>
          </div>
        )}

        <Group position="right" mt={10}>
          <Button color="cancel" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="filled"
            loading={isSubmitting}
            disabled={!form.isDirty() || isSubmitting}
          >
            Update Program
          </Button>
        </Group>
      </Stack>
    </form>
  );
}
